import React, { useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Box, CircularProgress, Tooltip } from '@mui/material';
import moment from 'moment';
import styled from 'styled-components';
import type { ProjectDate } from '../../app/feathers/project-dates/ProjectDate';

export interface CalendarItem {
  type: 'project' | 'election';
  label: string;
  start: moment.Moment;
  end?: moment.Moment;
  electionKey: string;
  electionName: string;
  stateName: string;
  /** e.g. "Georgia Primary" from state.name + prettified election type */
  electionSubtext: string;
  projectDate?: ProjectDateForCalendar;
}

interface ElectionForCalendar {
  key: string;
  name: string;
  electionType?: string | null;
  state?: string;
  stateName?: string;
  stateCode?: string;
  date?: string | Date | null;
  qualifyingDate?: string | Date | null;
  earlyVotingStart?: string | Date | null;
  earlyVotingEnd?: string | Date | null;
  electionImplementation?: {
    goLiveDate?: string | Date | null;
    profilingStart?: string | Date | null;
    raceScoutingStartDate?: string | Date | null;
    measureScoutingStartDate?: string | Date | null;
  } | null;
}

type ProjectDateForCalendar = Pick<ProjectDate, '_id' | 'election' | 'organization' | 'date' | 'aggregatedInformation'>;

function toMoment(d: string | Date | null | undefined): moment.Moment | null {
  if (d == null) return null;
  const m = moment.utc(d);
  return m.isValid() ? m : null;
}

function prettifyElectionType(electionType: string | null | undefined): string {
  if (!electionType) return '';
  const map: Record<string, string> = {
    primary: 'Primary',
    'primary-runoff': 'Primary runoff',
    general: 'General',
    'general-runoff': 'General runoff',
    special: 'Special',
  };
  return map[electionType] || electionType.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

function buildCalendarItems(
  elections: ElectionForCalendar[],
  stateFilterId: string | null,
  projectDates: ProjectDateForCalendar[],
  orgNamesByKey: Record<string, string>
): CalendarItem[] {
  const items: CalendarItem[] = [];

  const stateId = (e: ElectionForCalendar) => e.state;
  const stateName = (e: ElectionForCalendar) => e.stateName || e.stateCode || '';

  const filtered = stateFilterId
    ? elections.filter((e) => stateId(e) === stateFilterId)
    : elections;
  const projectDatesByElection = projectDates.reduce<Record<string, ProjectDateForCalendar[]>>((acc, projectDate) => {
    if (!projectDate.election) return acc;
    if (!acc[projectDate.election]) acc[projectDate.election] = [];
    acc[projectDate.election].push(projectDate);
    return acc;
  }, {});

  filtered.forEach((election) => {
    const name = election.name || election.key;
    const sn = stateName(election);
    const typeLabel = prettifyElectionType(election.electionType);
    const electionSubtext = [sn, typeLabel].filter(Boolean).join(' ') || name;
    const ei = election.electionImplementation;

    // Project dates (prominent)
    (projectDatesByElection[election.key] || []).forEach((projectDate) => {
      const goLive = toMoment(projectDate.date);
      if (!goLive) return;
      const orgName = projectDate.organization
        ? orgNamesByKey[projectDate.organization] || projectDate.organization
        : 'Branch';
      items.push({
        type: 'project',
        label: `${orgName} Go-live`,
        start: goLive,
        electionKey: election.key,
        electionName: name,
        stateName: sn,
        electionSubtext,
        projectDate,
      });
    });

    const qualifying = toMoment(election.qualifyingDate);
    if (qualifying) {
      items.push({
        type: 'project',
        label: 'Qualifying ends',
        start: qualifying,
        electionKey: election.key,
        electionName: name,
        stateName: sn,
        electionSubtext,
      });
    }
    if (ei) {
      const goLive = toMoment(ei.goLiveDate);
      if (goLive) {
        items.push({
          type: 'project',
          label: 'Target go-live',
          start: goLive,
          electionKey: election.key,
          electionName: name,
          stateName: sn,
          electionSubtext,
        });
      }
      const profilingStart = toMoment(ei.profilingStart);
      if (profilingStart) {
        items.push({
          type: 'project',
          label: 'Candidate profiling starts',
          start: profilingStart,
          electionKey: election.key,
          electionName: name,
          stateName: sn,
          electionSubtext,
        });
      }
      const raceScout = toMoment(ei.raceScoutingStartDate);
      if (raceScout) {
        items.push({
          type: 'project',
          label: 'Race scouting starts',
          start: raceScout,
          electionKey: election.key,
          electionName: name,
          stateName: sn,
          electionSubtext,
        });
      }
      const measureScout = toMoment(ei.measureScoutingStartDate);
      if (measureScout) {
        items.push({
          type: 'project',
          label: 'Measure scouting starts',
          start: measureScout,
          electionKey: election.key,
          electionName: name,
          stateName: sn,
          electionSubtext,
        });
      }
    }

    // Election dates (subtle)
    const electionDay = toMoment(election.date);
    if (electionDay) {
      items.push({
        type: 'election',
        label: 'Election day',
        start: electionDay,
        electionKey: election.key,
        electionName: name,
        stateName: sn,
        electionSubtext,
      });
    }
    const earlyStart = toMoment(election.earlyVotingStart);
    const earlyEnd = toMoment(election.earlyVotingEnd);
    if (earlyStart) {
      items.push({
        type: 'election',
        label: earlyEnd && !earlyEnd.isSame(earlyStart, 'day') ? 'Early voting' : 'Early voting',
        start: earlyStart,
        end: earlyEnd && !earlyEnd.isSame(earlyStart, 'day') ? earlyEnd : undefined,
        electionKey: election.key,
        electionName: name,
        stateName: sn,
        electionSubtext,
      });
    }
  });

  return items.sort((a, b) => a.start.valueOf() - b.start.valueOf());
}

function getMonthsToShow(): moment.Moment[] {
  const now = moment.utc();
  const year = now.year();
  const start = now.clone().subtract(1, 'month').startOf('month');
  const end = moment.utc([year, 11, 1]); // December 1 of current year
  const months: moment.Moment[] = [];
  let m = start.clone();
  while (m.isSameOrBefore(end, 'month')) {
    months.push(m.clone());
    m.add(1, 'month');
  }
  return months;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const RANGE_LANE_STEP_PX = 38;
const SINGLE_DAY_STACK_STEP_PX = 20;

const ScrollContainer = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: 4px 0;
`;

const MonthBlock = styled(Box)`
  margin-bottom: 40px;
  break-inside: avoid;
  background: #fafafa;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const MonthTitle = styled(Typography)`
  font-weight: 700;
  margin-bottom: 14px;
  font-size: 1.125rem;
  letter-spacing: -0.01em;
  color: #1a1a1a;
  text-align: center;
`;

const Grid = styled.div<{ rows: number }>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: 24px repeat(${(p) => p.rows}, minmax(36px, auto));
  gap: 2px;
  background: #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
`;

const Cell = styled.div<{
  header?: boolean;
  isToday?: boolean;
  $gridCol?: number;
  $gridRow?: number;
  $rangeInset?: number;
}>`
  background: ${(p) => (p.header ? '#f0f0f0' : p.isToday ? '#e3f2fd' : '#fff')};
  padding: 6px 8px;
  font-size: 0.8125rem;
  min-height: 36px;
  position: relative;
  ${(p) => p.$gridCol != null && p.$gridRow != null && `grid-column: ${p.$gridCol}; grid-row: ${p.$gridRow};`}
  ${(p) => p.header && 'font-weight: 600; font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.04em; color: #5f6368; padding: 2px 4px; min-height: 0; display: flex; align-items: center; justify-content: center; text-align: center;'}
  ${(p) => p.isToday && !p.header && 'box-shadow: inset 0 0 0 2px #1976d2;'}
  ${(p) => !p.header && p.$rangeInset != null && p.$rangeInset > 0 && `padding-bottom: ${6 + p.$rangeInset}px;`}
`;

const DayNumber = styled.span<{ isToday?: boolean }>`
  color: ${(p) => (p.isToday ? '#1976d2' : '#5f6368')};
  font-weight: ${(p) => (p.isToday ? 700 : 500)};
  margin-right: 6px;
  font-size: 0.8125rem;
`;

const ItemChip = styled(Link)<{ $prominent: boolean }>`
  font-size: 0.6875rem;
  padding: 3px 8px;
  margin: 2px 0;
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.25;
  cursor: pointer;
  text-decoration: none;
  display: block;
  &:hover { opacity: 0.9; }
  ${(p) =>
    p.$prominent
      ? `
  background: linear-gradient(135deg, #1565c0 0%, #1976d2 100%);
  color: #ffffff;
  font-weight: 700;
  box-shadow: 0 1px 2px rgba(21, 101, 192, 0.25);
  `
      : `
  background: #e3f2fd;
  color: #5f6368;
  border: 1px solid #bbdefb;
  font-weight: 500;
  `}
`;

const ItemChipLabel = styled.span<{ prominent?: boolean }>`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: ${(p) => (p.prominent ? 700 : 500)};
  color: ${(p) => (p.prominent ? '#ffffff' : 'inherit')};
`;

const ItemChipSubtext = styled.span<{ prominent: boolean }>`
  display: block;
  font-size: 0.6rem;
  font-weight: ${(p) => (p.prominent ? 700 : 500)};
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => (p.prominent ? '#ffffff' : 'inherit')};
  opacity: ${(p) => (p.prominent ? 1 : 0.85)};
`;

const ProjectDateProgressRow = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  align-items: center;
  margin-top: 5px;
  gap: 16px;
`;

const ProjectDateProgressItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const ProjectDateProgressLabel = styled.span`
  font-size: 0.55rem;
  font-weight: 700;
  line-height: 1;
  color: #ffffff;
`;

const ProjectDateProgressCircle = styled.span`
  position: relative;
  display: inline-flex;
  width: 16px;
  height: 16px;
`;

/** Spanning bar for date ranges - spans multiple grid columns */
const SpanningBar = styled(Link)<{
  $prominent: boolean;
  $gridColStart: number;
  $gridColEnd: number;
  $gridRow: number;
  $lane: number;
  $topOffset: number;
}>`
  grid-column: ${(p) => p.$gridColStart} / ${(p) => p.$gridColEnd};
  grid-row: ${(p) => p.$gridRow};
  align-self: end;
  margin: ${(p) => p.$topOffset}px 1px ${(p) => 2 + p.$lane * RANGE_LANE_STEP_PX}px 1px;
  padding: 3px 8px;
  cursor: pointer;
  text-decoration: none;
  &:hover { opacity: 0.9; }
  border-radius: 6px;
  font-size: 0.6875rem;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  z-index: ${(p) => 1 + p.$lane};
  ${(p) =>
    p.$prominent
      ? `
  background: linear-gradient(135deg, #1565c0 0%, #1976d2 100%);
  color: #ffffff;
  font-weight: 700;
  box-shadow: 0 1px 2px rgba(21, 101, 192, 0.25);
  `
      : `
  background: #e3f2fd;
  color: #5f6368;
  border: 1px solid #bbdefb;
  font-weight: 500;
  `}
`;

interface ElectionsCalendarViewProps {
  elections: ElectionForCalendar[];
  stateFilterId: string | null;
  projectDates: ProjectDateForCalendar[];
  orgNamesByKey: Record<string, string>;
}

const progressPercent = (completed: number | undefined, total: number | undefined) => (
  total ? Math.min(100, Math.round(((completed || 0) / total) * 100)) : 0
);

const progressTooltip = (
  completed: number | undefined,
  total: number | undefined,
  description: string
) => `${completed ?? '--'} of ${total ?? '--'} ${description}`;

const ProjectDateProgress = ({ projectDate }: { projectDate?: ProjectDateForCalendar }) => {
  const info = projectDate?.aggregatedInformation;
  if (!info) return null;

  const metrics = [
    {
      label: 'Scouting',
      completed: info.numScoutingDistrictsCompleted,
      total: info.numScoutingDistricts,
      description: 'districts scouted',
    },
    {
      label: 'Profiles',
      completed: info.numProfilesCompleted,
      total: info.numProfiles,
      description: 'profiles complete',
    },
    {
      label: 'Districts',
      completed: info.geodistrictsCollected,
      total: info.geodistrictsTotal,
      description: 'geodistricts collected',
    },
  ];

  return (
    <ProjectDateProgressRow>
      {metrics.map((metric) => (
        <Tooltip
          key={metric.label}
          title={progressTooltip(metric.completed, metric.total, metric.description)}
          arrow
        >
          <ProjectDateProgressItem>
            <ProjectDateProgressLabel>{metric.label}</ProjectDateProgressLabel>
            <ProjectDateProgressCircle>
              <CircularProgress
                variant="determinate"
                value={100}
                size={16}
                thickness={5}
                sx={{ color: 'rgba(255, 255, 255, 0.35)', position: 'absolute', left: 0 }}
              />
              <CircularProgress
                variant="determinate"
                value={progressPercent(metric.completed, metric.total)}
                size={16}
                thickness={5}
                sx={{ color: '#ffffff', position: 'absolute', left: 0 }}
              />
            </ProjectDateProgressCircle>
          </ProjectDateProgressItem>
        </Tooltip>
      ))}
    </ProjectDateProgressRow>
  );
};

export const ElectionsCalendarView: React.FC<ElectionsCalendarViewProps> = ({
  elections,
  stateFilterId,
  projectDates,
  orgNamesByKey,
}) => {
  const items = useMemo(
    () => buildCalendarItems(elections, stateFilterId, projectDates, orgNamesByKey),
    [elections, stateFilterId, projectDates, orgNamesByKey]
  );
  const months = useMemo(() => getMonthsToShow(), []);
  const today = useMemo(() => moment.utc(), []);
  const currentMonthRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    currentMonthRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []);

  const getItemsForDay = (monthStart: moment.Moment, dayOfMonth: number) => {
    const d = monthStart.clone().date(dayOfMonth);
    return items.filter((item) => {
      if (item.end) {
        return (
          (d.isSame(item.start, 'day') || d.isBetween(item.start, item.end, 'day', '[]')) &&
          d.isSame(monthStart, 'month')
        );
      }
      return d.isSame(item.start, 'day') && d.isSame(monthStart, 'month');
    });
  };

  const isToday = (monthStart: moment.Moment, dayOfMonth: number) => {
    return (
      monthStart.year() === today.year() &&
      monthStart.month() === today.month() &&
      dayOfMonth === today.date()
    );
  };

  /**
   * Get spanning segments for range items within a month.
   * Overlapping segments in the same week row are assigned separate lanes.
   */
  const getSpanningLayout = (
    monthStart: moment.Moment,
    rows: number,
    firstDow: number,
    singleDayCountByDay: Record<number, number>
  ): {
    segments: Array<{
      item: CalendarItem;
      row: number;
      colStart: number;
      colEnd: number;
      lane: number;
      topOffset: number;
    }>;
    rowLaneCounts: Record<number, number>;
  } => {
    const monthEnd = monthStart.clone().endOf('month');
    const rawSegments: Array<{
      item: CalendarItem;
      row: number;
      colStart: number;
      colEnd: number;
      topOffset: number;
    }> = [];
    const rangeItems = items.filter((i) => i.end && i.end.isAfter(i.start, 'day'));

    rangeItems.forEach((item) => {
      const rangeStart = item.start.clone().startOf('day');
      const rangeEnd = (item.end || item.start).clone().endOf('day');
      const isOutsideMonth = rangeEnd.isBefore(monthStart, 'day') || rangeStart.isAfter(monthEnd, 'day');
      if (isOutsideMonth) return;
      const startInMonth = rangeStart.isBefore(monthStart, 'month')
        ? monthStart.clone().startOf('month')
        : rangeStart;
      const endInMonth = rangeEnd.isAfter(monthEnd)
        ? monthEnd
        : rangeEnd;

      for (let r = 0; r < rows; r++) {
        const weekStart = monthStart
          .clone()
          .startOf('month')
          .add(r * 7 - firstDow, 'days');
        const weekEnd = weekStart.clone().add(6, 'days');
        const segStart = startInMonth.isBefore(weekStart) ? weekStart : startInMonth;
        const segEnd = endInMonth.isAfter(weekEnd) ? weekEnd : endInMonth;
        if (segStart.isAfter(segEnd)) continue;

        const colStart = segStart.day() + 1;
        const colEnd = segEnd.day() + 2;
        let maxSingleDayCount = 0;
        let dayCursor = segStart.clone().startOf('day');
        while (dayCursor.isSameOrBefore(segEnd, 'day')) {
          if (dayCursor.isSame(monthStart, 'month')) {
            const dayCount = singleDayCountByDay[dayCursor.date()] || 0;
            if (dayCount > maxSingleDayCount) maxSingleDayCount = dayCount;
          }
          dayCursor.add(1, 'day');
        }

        rawSegments.push({
          item,
          row: r + 2,
          colStart,
          colEnd,
          topOffset: maxSingleDayCount * SINGLE_DAY_STACK_STEP_PX,
        });
      }
    });

    const rowLaneCounts: Record<number, number> = {};
    const rowMap: Record<
      number,
      Array<{ item: CalendarItem; row: number; colStart: number; colEnd: number; topOffset: number }>
    > = {};
    rawSegments.forEach((seg) => {
      if (!rowMap[seg.row]) rowMap[seg.row] = [];
      rowMap[seg.row].push(seg);
    });

    const segments: Array<{
      item: CalendarItem;
      row: number;
      colStart: number;
      colEnd: number;
      lane: number;
      topOffset: number;
    }> = [];

    Object.entries(rowMap).forEach(([rowStr, rowSegments]) => {
      const row = Number(rowStr);
      const sorted = rowSegments.sort((a, b) => {
        if (a.colStart !== b.colStart) return a.colStart - b.colStart;
        return a.colEnd - b.colEnd;
      });

      // Track the "exclusive end" column currently occupied per lane.
      const laneEndByIndex: number[] = [];

      sorted.forEach((seg) => {
        let lane = laneEndByIndex.findIndex((laneEnd) => seg.colStart >= laneEnd);
        if (lane === -1) {
          lane = laneEndByIndex.length;
          laneEndByIndex.push(seg.colEnd);
        } else {
          laneEndByIndex[lane] = seg.colEnd;
        }

        segments.push({ ...seg, lane });
      });

      rowLaneCounts[row] = laneEndByIndex.length;
    });

    return { segments, rowLaneCounts };
  };

  return (
    <ScrollContainer>
      {months.map((monthStart) => {
        const daysInMonth = monthStart.daysInMonth();
        const firstDow = monthStart.day();
        const totalCells = firstDow + daysInMonth;
        const rows = Math.ceil(totalCells / 7);
        const dayItemsByDay: Record<number, CalendarItem[]> = {};
        const singleDayCountByDay: Record<number, number> = {};
        for (let day = 1; day <= daysInMonth; day++) {
          const dayItems = getItemsForDay(monthStart, day);
          dayItemsByDay[day] = dayItems;
          singleDayCountByDay[day] = dayItems.filter(
            (item) => !item.end || item.start.isSame(item.end, 'day')
          ).length;
        }

        const isCurrentMonth =
          monthStart.year() === today.year() && monthStart.month() === today.month();
        const { segments: spanningSegments, rowLaneCounts } = getSpanningLayout(
          monthStart,
          rows,
          firstDow,
          singleDayCountByDay
        );

        return (
          <MonthBlock
            key={monthStart.format('YYYY-MM')}
            ref={isCurrentMonth ? currentMonthRef : undefined}
          >
            <MonthTitle variant="subtitle1">
              {monthStart.format('MMMM YYYY')}
            </MonthTitle>
            <Grid rows={rows}>
              {WEEKDAYS.map((wd, i) => (
                <Cell key={wd} header $gridCol={i + 1} $gridRow={1}>
                  {wd}
                </Cell>
              ))}
              {Array.from({ length: firstDow }, (_, i) => (
                <Cell key={`empty-${i}`} $gridCol={i + 1} $gridRow={2} />
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const gridIndex = firstDow + day;
                const gridRow = Math.ceil(gridIndex / 7) + 1;
                const gridCol = ((gridIndex - 1) % 7) + 1;
                const dayItems = dayItemsByDay[day] || [];
                const todayCell = isToday(monthStart, day);
                const singleDayItems = dayItems.filter(
                  (item) => !item.end || item.start.isSame(item.end, 'day')
                );

                return (
                  <Cell
                    key={day}
                    isToday={todayCell}
                    $gridCol={gridCol}
                    $gridRow={gridRow}
                    $rangeInset={(rowLaneCounts[gridRow] || 0) * RANGE_LANE_STEP_PX}
                  >
                    <DayNumber isToday={todayCell}>{day}</DayNumber>
                    {singleDayItems.map((item, idx) => (
                      <ItemChip
                        key={`${item.electionKey}-${item.label}-${item.start.valueOf()}-${idx}`}
                        to={`/elections/${item.electionKey}`}
                        $prominent={item.type === 'project'}
                        title={item.projectDate ? undefined : `${item.label} – ${item.electionName}${item.stateName ? ` (${item.stateName})` : ''}`}
                      >
                        <ItemChipLabel prominent={item.type === 'project'}>
                          {item.label}
                        </ItemChipLabel>
                        <ItemChipSubtext prominent={item.type === 'project'}>
                          {item.electionSubtext}
                        </ItemChipSubtext>
                        {item.projectDate ? (
                          <ProjectDateProgress projectDate={item.projectDate} />
                        ) : null}
                      </ItemChip>
                    ))}
                  </Cell>
                );
              })}
              {spanningSegments.map((seg, idx) => (
                <SpanningBar
                  key={`${seg.item.electionKey}-${seg.item.label}-${seg.item.start.valueOf()}-${seg.row}-${idx}`}
                  to={`/elections/${seg.item.electionKey}`}
                  $prominent={seg.item.type === 'project'}
                  $gridColStart={seg.colStart}
                  $gridColEnd={seg.colEnd}
                  $gridRow={seg.row}
                  $lane={seg.lane}
                  $topOffset={seg.topOffset}
                  title={`${seg.item.label} – ${seg.item.electionName}${seg.item.stateName ? ` (${seg.item.stateName})` : ''}`}
                >
                  <ItemChipLabel prominent={seg.item.type === 'project'}>
                    {seg.item.label}
                  </ItemChipLabel>
                  <ItemChipSubtext prominent={seg.item.type === 'project'}>
                    {seg.item.electionSubtext}
                  </ItemChipSubtext>
                </SpanningBar>
              ))}
            </Grid>
          </MonthBlock>
        );
      })}
    </ScrollContainer>
  );
};
